import { useState, type FormEvent } from 'react';
import {
  useTourDiscounts,
  type TourDiscount,
  type TourDiscountInput,
  isDiscountExpired,
} from '@/hooks/useTourDiscounts';
import { useTours } from '@/hooks/useTours';
import SelectChevron from '@/components/base/SelectChevron';

interface FormState {
  id: string | null;
  type: 'percent' | 'fixed';
  value: string;
  active: boolean;
  tour_id: string | null; // null = all tours
  expires_at: string; // '' = no expiry
}

const emptyForm: FormState = {
  id: null,
  type: 'percent',
  value: '',
  active: true,
  tour_id: null,
  expires_at: '',
};

export default function TourDiscountManager() {
  const { discounts, loading, error, createDiscount, updateDiscount, toggleActive, deleteDiscount } =
    useTourDiscounts();
  const { tours } = useTours();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const isEditing = !!form.id;

  const tourLabel = (tourId: string | null): string => {
    if (!tourId) return 'All tours';
    const t = tours.find((x) => x.id === tourId);
    return t ? t.title : 'Unknown tour';
  };

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (d: TourDiscount) => {
    setForm({
      id: d.id,
      type: d.type,
      value: String(d.value),
      active: d.active,
      tour_id: d.tour_id,
      expires_at: d.expires_at || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const value = Number(form.value);
    if (!form.value || Number.isNaN(value) || value <= 0) {
      setFormError('Please enter a valid discount value greater than 0.');
      return;
    }
    if (form.type === 'percent' && value > 100) {
      setFormError('Percent discount cannot exceed 100%.');
      return;
    }

    setSaving(true);
    const input: TourDiscountInput = {
      type: form.type,
      value,
      active: form.active,
      tour_id: form.tour_id,
      expires_at: form.expires_at || null,
    };

    const result = isEditing
      ? await updateDiscount(form.id as string, input)
      : await createDiscount(input);
    setSaving(false);

    if (result.error) {
      setFormError(result.error);
      return;
    }

    setShowForm(false);
    setToast(isEditing ? 'Tour discount updated.' : 'Tour discount created.');
    window.setTimeout(() => setToast(''), 3000);
  };

  const handleToggle = async (d: TourDiscount) => {
    setToast('');
    const { error: err } = await toggleActive(d.id, !d.active);
    setToast(
      err ? 'Failed to update. Please try again.' : `Discount ${d.active ? 'deactivated' : 'activated'}.`,
    );
    window.setTimeout(() => setToast(''), 3000);
  };

  const handleDelete = async (d: TourDiscount) => {
    if (!window.confirm('Delete this tour discount? This cannot be undone.')) return;
    setToast('');
    const { error: err } = await deleteDiscount(d.id);
    setToast(err ? 'Failed to delete. Please try again.' : 'Discount deleted.');
    window.setTimeout(() => setToast(''), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-secondary-500">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <span className="text-sm">Loading tour discounts...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">Something went wrong</h3>
        <p className="mt-1 text-sm text-secondary-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-secondary-900">Tour price discounts</h2>
          <p className="text-sm text-secondary-500 mt-0.5">
            Mark down tour prices directly — the new price shows on the site with the old price
            crossed out. {discounts.length} discount{discounts.length !== 1 ? 's' : ''} total.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line text-base" />
          New tour discount
        </button>
      </div>

      {toast && (
        <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          <i className="ri-checkbox-circle-line text-base" />
          {toast}
        </div>
      )}

      {/* Discounts list */}
      {discounts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-secondary-200">
          <div className="w-16 h-16 mx-auto rounded-full bg-secondary-100 flex items-center justify-center">
            <i className="ri-percent-line text-2xl text-secondary-500" />
          </div>
          <h3 className="mt-4 font-heading text-lg font-bold text-secondary-900">
            No tour discounts yet
          </h3>
          <p className="mt-1 text-sm text-secondary-500">
            Create a discount to mark down a tour (or every tour) directly on its price.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {discounts.map((d) => (
            <div
              key={d.id}
              className="bg-white rounded-lg border border-secondary-200 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary-900">
                    <i className="ri-price-tag-3-line text-secondary-500" />
                    {tourLabel(d.tour_id)}
                  </span>
                  <span
                    className={`text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap ${
                      d.type === 'percent'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-accent-100 text-accent-900'
                    }`}
                  >
                    {d.type === 'percent'
                      ? `${Number(d.value)}% off`
                      : `$${Number(d.value).toLocaleString()} off`}
                  </span>
                  <span
                    className={`text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap ${
                      d.active ? 'bg-green-100 text-green-700' : 'bg-secondary-100 text-secondary-500'
                    }`}
                  >
                    {d.active ? 'Active' : 'Inactive'}
                  </span>
                  {isDiscountExpired(d) && (
                    <span className="text-xs font-semibold rounded-full px-2.5 py-0.5 whitespace-nowrap bg-red-100 text-red-600">
                      Expired
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[11px] text-secondary-400">
                  {d.tour_id
                    ? 'This discount applies only to this tour.'
                    : 'This discount applies to every tour.'}
                  {d.expires_at ? ` Valid until ${d.expires_at}.` : ' No expiry date set.'}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleToggle(d)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-600 border border-secondary-300 hover:border-secondary-400 rounded-md px-3 py-1.5 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={d.active ? 'ri-toggle-line' : 'ri-toggle-fill'} />
                  {d.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => openEdit(d)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-secondary-300 text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                >
                  <i className="ri-pencil-line text-base" />
                </button>
                <button
                  onClick={() => handleDelete(d)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-red-200 text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-bold text-secondary-900">
                {isEditing ? 'Edit tour discount' : 'New tour discount'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-secondary-500 hover:bg-secondary-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                  Apply to
                </label>
                <div className="relative">
                  <select
                    value={form.tour_id ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, tour_id: e.target.value === '' ? null : e.target.value })
                    }
                    className="appearance-none w-full border border-secondary-200 rounded-md pl-3.5 pr-8 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition cursor-pointer"
                  >
                    <option value="">All tours</option>
                    {tours.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                  <SelectChevron className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary-400 text-sm" />
                </div>
                <p className="mt-1 text-[11px] text-secondary-400">
                  Pick a single tour, or leave as "All tours" to mark down every tour.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">Type</label>
                  <div className="relative">
                    <select
                      value={form.type}
                      onChange={(e) =>
                        setForm({ ...form, type: e.target.value as 'percent' | 'fixed' })
                      }
                      className="appearance-none w-full border border-secondary-200 rounded-md pl-3.5 pr-8 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition cursor-pointer"
                    >
                      <option value="percent">Percent (%)</option>
                      <option value="fixed">Fixed ($)</option>
                    </select>
                    <SelectChevron className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary-400 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step={form.type === 'percent' ? '1' : '0.01'}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    placeholder={form.type === 'percent' ? '10' : '15'}
                    className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-secondary-700 mb-1.5 block">
                  Expiry date
                </label>
                <input
                  type="date"
                  value={form.expires_at}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="w-full border border-secondary-200 rounded-md px-3.5 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                />
                <p className="mt-1 text-[11px] text-secondary-400">
                  Leave empty for no expiry. The discount stops applying after this date.
                </p>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
                />
                <span className="text-sm text-secondary-700">Active (applied right away)</span>
              </label>

              {formError && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-md px-3.5 py-2.5">
                  <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 shrink-0" />
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="inline-flex items-center justify-center gap-2 border border-secondary-300 hover:border-secondary-400 text-secondary-700 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  {saving ? (
                    <i className="ri-loader-4-line animate-spin text-base" />
                  ) : (
                    <>{isEditing ? 'Save changes' : 'Create discount'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}